/**
 * RFC-149: MarketingSection — title + kicker + alignment + padding rhythm.
 * RFC-150: `surface` variant for per-page visual rhythm.
 *
 * Use `density="airy"` for spacious sections, `dense` for tighter content.
 *
 * `surface` controls the section's background:
 *   - `default`  — transparent, inherits page bg (current behavior).
 *   - `inset`    — `bg-muted/40` rounded panel; gives a quiet pause.
 *   - `contrast` — `bg-foreground text-background` panel; high-impact.
 *                  Use AT MOST ONCE per page (the page is the spec, not CI).
 */
import type { ReactNode } from 'react';
import { MotionReveal } from './MotionReveal';

interface MarketingSectionProps {
  title?: ReactNode;
  kicker?: ReactNode;
  description?: ReactNode;
  align?: 'center' | 'start';
  density?: 'airy' | 'dense';
  surface?: 'default' | 'inset' | 'contrast';
  /** Wrap children in a max-width container. Defaults to true. */
  contained?: boolean;
  id?: string;
  className?: string;
  children: ReactNode;
}

const DENSITY_CLASS: Record<NonNullable<MarketingSectionProps['density']>, string> = {
  airy: 'py-20 md:py-28',
  dense: 'py-12 md:py-16',
};

/**
 * Surface classes apply to the inner panel, not the outer <section>, so the
 * page background stays continuous and only the contained block changes tone.
 */
const SURFACE_CLASS: Record<NonNullable<MarketingSectionProps['surface']>, string> = {
  default: '',
  inset: 'bg-muted/40 rounded-3xl px-6 md:px-10 py-12 md:py-16',
  contrast: 'bg-foreground text-background rounded-3xl px-6 md:px-10 py-12 md:py-16',
};

export function MarketingSection({
  title,
  kicker,
  description,
  align = 'center',
  density = 'airy',
  surface = 'default',
  contained = true,
  id,
  className,
  children,
}: MarketingSectionProps) {
  const surfaceClass = SURFACE_CLASS[surface];
  const isToned = surface !== 'default';
  const inner = (
    <>
      {(kicker || title || description) && (
        <header className={`mb-12 ${align === 'center' ? 'text-center mx-auto max-w-2xl' : 'text-left max-w-2xl'}`}>
          {kicker ? (
            <MotionReveal
              as="p"
              className={`text-sm font-semibold uppercase tracking-wider mb-3 ${isToned && surface === 'contrast' ? 'text-background/80' : 'text-primary'}`}
            >
              {kicker}
            </MotionReveal>
          ) : null}
          {title ? (
            <MotionReveal as="h2" className="text-3xl md:text-4xl font-bold mb-4">
              {title}
            </MotionReveal>
          ) : null}
          {description ? (
            <MotionReveal
              as="p"
              className={`text-lg ${surface === 'contrast' ? 'text-background/80' : 'text-muted-foreground'}`}
              delayMs={80}
            >
              {description}
            </MotionReveal>
          ) : null}
        </header>
      )}
      {children}
    </>
  );

  return (
    <section id={id} className={`container mx-auto px-4 ${DENSITY_CLASS[density]} ${className ?? ''}`}>
      {contained ? (
        <div className={`max-w-5xl mx-auto ${surfaceClass}`}>{inner}</div>
      ) : isToned ? (
        <div className={surfaceClass}>{inner}</div>
      ) : (
        inner
      )}
    </section>
  );
}
